import { yupResolver } from '@hookform/resolvers/yup';
import ArrowCircleDown from '@mui/icons-material/ArrowCircleDown';
import Check from '@mui/icons-material/Check';
import Edit from '@mui/icons-material/Edit';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  InvoiceCategoryFilter,
  OrderState,
  RateOwnerType,
  RateType,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { snakeCase } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import { Chip } from '~components/Chip/Chip';
import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import { AutocompleteAsyncFormField } from '~components/FormFields/AutocompleteAsyncFormField';
import { AutocompleteFormField } from '~components/FormFields/AutocompleteFormField';
import { TextFormField } from '~components/FormFields/TextFormField';
import LinkChip from '~components/LinkChip/LinkChip';
import Menu, { MenuHandler } from '~components/Menu/Menu';
import { OverflowAwareText } from '~components/Order/ordersDispatchStyledComponents';
import PayCommissionControl from '~components/PayCommission/PayCommissionControl';
import { payCommissionFieldError } from '~components/PayCommission/payCommissionFieldError';
import { FeatureFlags } from '~constants/featureFlags';
import { useCompanyCurrency } from '~hooks/useCompanyCurrency/useCompanyCurrency';
import useInvoiceLineItems from '~hooks/useInvoiceLineItems/useInvoiceLineItems';
import { Invoice } from '~hooks/useInvoices/models';
import useInvoices from '~hooks/useInvoices/useInvoices';
import { Job } from '~hooks/useJob/models';
import { useJob } from '~hooks/useJob/useJob';
import { useRates } from '~hooks/useRates/useRates';
import { SiteRateSchedule } from '~hooks/useSiteRateSchedule/models';
import { useSiteRateSchedule } from '~hooks/useSiteRateSchedule/useSiteRateSchedule';
import { DriverPayUtils } from '~pages/Approvals/DriverPay/driverPayUtils';
import {
  RateDetailsDTO,
  rateDetailsSchema,
} from '~pages/Sales/Orders/NewOrderForm/schema';
import { rateToRateBasicWithValue } from '~pages/Sales/Orders/NewOrderForm/utils';
import {
  getFormattedRateValue,
  getRateTypeLabel,
  getRateTypeOptions,
} from '~pages/Settings/RatesManagement/rateUtils';
import { ROUTES } from '~router/routePaths';
import { useStores } from '~store';
import { deriveScheduleLabel } from '~utils/deriveScheduleLabel';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';
import {
  invoicePayCommission,
  isGrossRateUnchanged,
  payCommissionPatch,
  resolveRateEditableSides,
  resolveRateSubmit,
  savedPayCommissionOverride,
} from '~utils/jobs/job-utils';
import { getFriendlySettlementId } from '~utils/settlements/settlement-utils';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { payableInvoiceCategories } from './constants';

interface SiteRateScheduleOption {
  id: string;
  name: string;
  _siteRateSchedule: SiteRateSchedule;
}

interface VendorRatePanelProps {
  invoice: Invoice;
  job?: Job;
  utils: DriverPayUtils;
  category: InvoiceCategoryFilter;
  canEdit: boolean;
  hideInvoiceApproval?: boolean;
  isSettlementApproved?: boolean;
  onEditChange: (isEditing: boolean) => void;
  reload?: () => void;
}

const VendorRatePanel = observer(function VendorRatePanel({
  invoice,
  job,
  utils,
  category,
  canEdit,
  hideInvoiceApproval = false,
  isSettlementApproved = true,
  onEditChange,
  reload,
}: VendorRatePanelProps) {
  const theme = useTheme();
  const isReceivable = category === InvoiceCategoryFilter.RECEIVABLES;
  const hasPayableCommissionSide =
    payableInvoiceCategories.includes(category) &&
    Boolean(invoice.driver || invoice.vendorAccount);
  const { currencyFormatter } = useCompanyCurrency();
  const { getAccountRatesUsingOwnerType } = useRates();
  const { getJobById } = useJob();
  const { updateInvoiceRate } = useInvoices();
  const { getAllInvoiceLineItems } = useInvoiceLineItems();
  const { getAllProjectSiteRateSchedules, getAllOrderSiteRateSchedules } =
    useSiteRateSchedule();
  const [isUpdating, setIsUpdating] = useState(false);
  const [isApprovingInvoice, setIsApprovingInvoice] = useState(false);
  const [unapproveInvoiceState, setUnapproveInvoiceState] = useState<{
    isDialogOpen: boolean;
    isUnapproving: boolean;
  }>({ isDialogOpen: false, isUnapproving: false });
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  // `Job.company` is the order owner; a mismatch means viewer is a sub-dispatch vendor.
  const isOrderOwner = job?.company ? companyId === job.company.id : false;
  const isCustomerRateReadOnly = isReceivable && !isOrderOwner;

  // Under load-level pricing, un-approval retains the approved rates (re-flowing only on a later
  // rate-card change) rather than refreshing to the latest — so the unapprove sync-warning differs.
  const isLoadLevelPricingOn = useFeatureFlag({
    featureFlagKey: FeatureFlags.loadLevelPricing,
  });
  // Sub-dispatch vendors can't read project/order site_rate_schedules; collapse the
  // schedule-aware paths for them.
  const canReadSiteRateSchedules = isOrderOwner;

  const isInternalDriver = Boolean(
    invoice.driver && (!invoice.vendorAccount || !invoice.vendorAccount.connectedCompany),
  );

  const isCompletedOrder = job?.order?.state === OrderState.COMPLETED;
  const commissionParty = isInternalDriver ? 'driver' : 'vendor';
  const commission = invoicePayCommission(invoice, commissionParty);
  const savedCommissionOverride = savedPayCommissionOverride(commission);
  const rateFieldPrefix = isReceivable
    ? 'customer'
    : isInternalDriver
      ? 'driver'
      : 'vendor';

  const [siteRateSchedules, setSiteRateSchedules] = useState<SiteRateSchedule[]>([]);
  const [hasFetchedSiteRates, setHasFetchedSiteRates] = useState(false);

  useEffect(() => {
    if (!canReadSiteRateSchedules) {
      setHasFetchedSiteRates(true);
      return;
    }

    const fetchSiteRates = async () => {
      let schedules: SiteRateSchedule[] = [];

      if (isCompletedOrder && job?.order?.id) {
        schedules = (await getAllOrderSiteRateSchedules(companyId, job.order.id)) ?? [];
      } else if (job?.project?.id) {
        schedules = (await getAllProjectSiteRateSchedules(job.project.id)) ?? [];
      }

      setSiteRateSchedules(schedules);
      setHasFetchedSiteRates(true);
    };

    fetchSiteRates();
  }, [
    canReadSiteRateSchedules,
    isCompletedOrder,
    job?.order?.id,
    job?.project?.id,
    companyId,
  ]);

  const useSiteRateScheduleOptions =
    canReadSiteRateSchedules && hasFetchedSiteRates && siteRateSchedules.length > 0;

  const [selectedSiteRateScheduleId, setSelectedSiteRateScheduleId] = useState<
    string | null
  >(canReadSiteRateSchedules ? (invoice.siteRateSchedule?.id ?? null) : null);

  const [isEditing, setIsEditing] = useState(false);
  const canEditLineItems = invoice.editable;
  // Approving or un-approving from this panel is exactly what moves the per-side rate lock, and no
  // call site refetches the job, so prefer a copy re-read after that action over the stale prop.
  const [refreshedJob, setRefreshedJob] = useState<Job | null>(null);
  const effectiveJob = refreshedJob ?? job;
  // `invoice.editable` is state-based; the job's per-side rate lock is approval-based. They diverge
  // while an approved invoice is out for collection — there the backend drops a locked side's rate
  // and still answers 200, so gating on `editable` alone offers a field whose save silently no-ops.
  const rateEditableSides = effectiveJob
    ? resolveRateEditableSides(
        effectiveJob,
        Boolean(effectiveJob.vendorJobAssignment?.id),
      )
    : null;
  const isRateApprovalLocked = rateEditableSides
    ? !(isReceivable ? rateEditableSides.customer : rateEditableSides.assignee)
    : false;
  const canEditCommission =
    hasPayableCommissionSide &&
    commission.locked === false &&
    canEditLineItems &&
    !isRateApprovalLocked &&
    userStore.getPermissions().canEditRate;
  const isApprovable = invoice.approvable;
  const isUnapprovableInvoice = invoice.unapprovable;
  const hasSettlement = Boolean(invoice.settlement);

  const buildTargetRate = () => {
    if (canReadSiteRateSchedules && invoice.siteRateSchedule) {
      return {
        id: invoice.siteRateSchedule.id,
        name: deriveScheduleLabel(invoice.siteRateSchedule),
        type: invoice.rateType ?? '',
        rate: invoice.rateValue ?? 0,
      };
    }
    return invoice.rate ?? null;
  };

  const selectedSiteRateScheduleName = (() => {
    if (!canReadSiteRateSchedules) return null;
    if (!selectedSiteRateScheduleId) return null;
    if (invoice.siteRateSchedule?.id === selectedSiteRateScheduleId) {
      return deriveScheduleLabel(invoice.siteRateSchedule);
    }
    const srs = siteRateSchedules.find((s) => s.id === selectedSiteRateScheduleId);
    return srs ? deriveScheduleLabel(srs) : null;
  })();

  const fetchCompanyRates = async (search: string) => {
    // Driver scoping only makes sense when the driver lives in the viewer's company.
    const isDriverInViewerCompany = isOrderOwner || isInternalDriver;
    const ownerType = isReceivable
      ? RateOwnerType.CUSTOMER
      : isInternalDriver
        ? RateOwnerType.DRIVER
        : RateOwnerType.VENDOR;

    return await getAccountRatesUsingOwnerType({
      driverId: isDriverInViewerCompany ? invoice.driver?.id : undefined,
      ownerType,
      query: search,
      accountId: isReceivable
        ? (invoice.customerAccount?.id ?? undefined)
        : isInternalDriver
          ? undefined
          : (invoice.vendorAccount?.id ?? undefined),
      includeDefaultRates: true,
      companyId,
    });
  };

  const fetchSiteRateScheduleOptions = ({
    query,
  }: {
    query?: string;
  }): { data: SiteRateScheduleOption[] } => {
    const lowerSearch = (query ?? '').toLowerCase();
    const filtered = siteRateSchedules.filter((srs) => {
      if (!lowerSearch) return true;
      return deriveScheduleLabel(srs).toLowerCase().includes(lowerSearch);
    });

    return {
      data: filtered.map((srs) => ({
        id: srs.id,
        name: deriveScheduleLabel(srs),
        _siteRateSchedule: srs,
      })),
    };
  };

  const fetchRates = useSiteRateScheduleOptions
    ? fetchSiteRateScheduleOptions
    : fetchCompanyRates;

  const rateTypeOptions = getRateTypeOptions(
    isReceivable ? RateOwnerType.CUSTOMER : RateOwnerType.VENDOR,
  );
  const {
    control,
    handleSubmit,
    formState: { dirtyFields, errors },
    setError,
    setValue,
    watch,
    reset,
  } = useForm<RateDetailsDTO>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: yupResolver(rateDetailsSchema),
    defaultValues: {
      targetRate: buildTargetRate(),
      targetRateType: invoice.rateType
        ? { label: getRateTypeLabel(invoice.rateType), value: invoice.rateType }
        : null,
      targetRateValue: invoice.rateValue ?? 0,
      targetPayCommissionPercentage: savedCommissionOverride,
    },
  });

  const { targetRateType, targetRateValue, targetRate } = watch();

  useEffect(() => {
    if (targetRateValue !== targetRate?.rate) {
      setValue('targetRate', null);
      setSelectedSiteRateScheduleId(null);
    }
  }, [targetRateValue]);

  useEffect(() => {
    if (targetRateType?.value !== targetRate?.type) {
      setValue('targetRate', null);
      setSelectedSiteRateScheduleId(null);
    }
  }, [targetRateType]);

  const handleClearTargetRate = useCallback(() => {
    setValue('targetRate', null);
    setValue('targetRateType', null);
    setValue('targetRateValue', 0);
    setSelectedSiteRateScheduleId(null);
  }, []);

  const handleEditChange = (isEditing: boolean) => {
    setIsEditing(isEditing);
    onEditChange(isEditing);
  };

  const onSubmit = async (data: RateDetailsDTO) => {
    // Defense-in-depth: edit button is hidden for sub-dispatch vendors; seal the submit path too.
    // A lock can also land while the editor is open, so re-check it here rather than only on render.
    if (!canEdit || !invoice.editable || isCustomerRateReadOnly || isRateApprovalLocked) {
      return;
    }

    const grossUnchanged =
      isGrossRateUnchanged(
        {
          rateId: data.targetRate?.id,
          rateType: data.targetRateType?.value,
          // An untouched field reads as 0 on an invoice with no saved rate, so
          // only a value the user actually edited can mark the gross changed.
          rateValue: dirtyFields.targetRateValue
            ? data.targetRateValue
            : invoice.rateValue,
        },
        {
          rateId: buildTargetRate()?.id,
          rateType: invoice.rateType,
          rateValue: invoice.rateValue,
        },
      ) && selectedSiteRateScheduleId === (invoice.siteRateSchedule?.id ?? null);
    const { commissionChanged, omitGrossRate, payCommissionPercentage } =
      resolveRateSubmit({
        grossUnchanged,
        rateType: data.targetRateType?.value,
        savedPercentage: savedCommissionOverride,
        submittedPercentage: data.targetPayCommissionPercentage,
      });
    if (commissionChanged && !canEditCommission) {
      setError('targetPayCommissionPercentage', {
        message: t('pay_commission.not_editable'),
      });
      return;
    }

    const grossRatePatch = omitGrossRate
      ? {}
      : {
          rate_id: selectedSiteRateScheduleId ? null : (data.targetRate?.id ?? null),
          rate_type: data.targetRateType?.value as RateType,
          rate_value: data.targetRateValue ?? null,
          site_rate_schedule_id: selectedSiteRateScheduleId,
        };

    setIsUpdating(true);
    try {
      await updateInvoiceRate({
        invoiceId: String(invoice.id),
        ...grossRatePatch,
        ...(hasPayableCommissionSide
          ? payCommissionPatch(commissionParty, payCommissionPercentage)
          : {}),
      });
      await getAllInvoiceLineItems(
        companyId,
        String(invoice.id),
        invoice.materialRateItemized,
      );
      await reload?.();
      handleEditChange(false);
    } catch (error) {
      // The failure has already been reported and toasted by connection.handleRequestError, which
      // rethrows. React Hook Form's handleSubmit propagates whatever onValid throws and the Save
      // button does not await it, so letting it escape here surfaces as an unhandled rejection
      // instead of a handled error. Staying in edit mode is intentional — the user keeps their
      // input and can retry.
      const commissionError = payCommissionFieldError(error, commissionParty);
      if (commissionError) {
        setError('targetPayCommissionPercentage', {
          message: commissionError,
          type: 'server',
        });
      }
    } finally {
      setIsUpdating(false);
    }
  };

  // Re-read the lock rather than clearing it optimistically: a sibling invoice on the same side can
  // still hold it, so only the server knows whether this approval change actually released it.
  // `getJobById` is omitted from the deps because `useJob` returns it unmemoized.
  const refreshRateLock = useCallback(async () => {
    if (!job?.id) {
      return;
    }

    const updatedJob = await getJobById(job.id, companyId);

    if (updatedJob) {
      setRefreshedJob(updatedJob);
    }
  }, [job?.id, companyId]);

  useEffect(() => {
    setRefreshedJob(null);
  }, [job?.id]);

  const handleApproveInvoiceClick = useCallback(async () => {
    try {
      setIsApprovingInvoice(true);
      await utils.approveInvoice(invoice);
      await refreshRateLock();
    } finally {
      setIsApprovingInvoice(false);
    }
  }, [utils.approveInvoice, refreshRateLock]);

  const handleConfirmUnapproveInvoiceClick = useCallback(async () => {
    try {
      setUnapproveInvoiceState((state) => ({ ...state, isUnapproving: true }));
      await utils.unapproveInvoice(invoice);
      await refreshRateLock();
    } finally {
      setUnapproveInvoiceState({ isDialogOpen: false, isUnapproving: false });
    }
  }, [invoice, refreshRateLock]);

  const resetToSavedRate = () => {
    reset({
      targetRate: buildTargetRate(),
      targetRateType: invoice.rateType
        ? { label: getRateTypeLabel(invoice.rateType), value: invoice.rateType }
        : null,
      targetRateValue: invoice.rateValue ?? 0,
      targetPayCommissionPercentage: savedCommissionOverride,
    });
    setSelectedSiteRateScheduleId(
      canReadSiteRateSchedules ? (invoice.siteRateSchedule?.id ?? null) : null,
    );
  };

  useEffect(resetToSavedRate, [JSON.stringify(invoice), canReadSiteRateSchedules]);

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 3,
          width: '75%',
        }}
      >
        {isEditing ? (
          <AutocompleteAsyncFormField
            name="targetRate"
            dataUi="page-approvals__vendor-rate-panel__rate-card-input"
            control={control}
            label={t('approvals.driver_pay.tabs.driver_pay.rate_card')}
            getLabel={(rate) => rate.name}
            getValue={(rate) => rate.id}
            asyncCallback={fetchRates}
            errors={errors}
            clearable
            onClear={handleClearTargetRate}
            onSelect={(item) => {
              if (useSiteRateScheduleOptions && item._siteRateSchedule) {
                const srs = item._siteRateSchedule as SiteRateSchedule;
                const rateTypeKey =
                  `${rateFieldPrefix}RateType` as keyof SiteRateSchedule;
                const rateValueKey =
                  `${rateFieldPrefix}RateValue` as keyof SiteRateSchedule;
                const rateType = srs[rateTypeKey] as RateType | null;
                const rateValue = srs[rateValueKey] as number | null;

                const nextTargetRate = {
                  id: srs.id,
                  name: item.name,
                  type: rateType ?? '',
                  rate: rateValue ?? 0,
                };

                // Write targetRate alongside type/value so the divergence
                // useEffects below see a consistent snapshot. Otherwise the
                // Controller's onChange runs after the autocomplete's internal
                // `await onSelect(...)` microtask, which lets those useEffects
                // fire against a stale targetRate and erroneously clear
                // selectedSiteRateScheduleId.
                setValue('targetRate', nextTargetRate);
                setSelectedSiteRateScheduleId(srs.id);
                setValue(
                  'targetRateType',
                  rateType
                    ? {
                        label: getRateTypeLabel(rateType),
                        value: rateType,
                      }
                    : null,
                );
                setValue('targetRateValue', rateValue ?? 0);

                return nextTargetRate;
              }

              const rateBasicWithValue = rateToRateBasicWithValue(item);

              const nextTargetRate = {
                id: item.id,
                name: item.name,
                type: item.type.value,
                rate: rateBasicWithValue.rate,
              };

              setValue('targetRate', nextTargetRate);
              setSelectedSiteRateScheduleId(null);
              setValue('targetRateType', {
                label: getRateTypeLabel(item.type.value as RateType),
                value: item.type.value,
              });
              setValue('targetRateValue', rateBasicWithValue.rate);

              return nextTargetRate;
            }}
          />
        ) : (
          <PanelField
            dataUi="page-approvals__vendor-rate-panel__rate-card-field"
            label={t('approvals.driver_pay.tabs.driver_pay.rate_card')}
            content={selectedSiteRateScheduleName ?? invoice.rate?.name ?? ''}
          />
        )}

        {isEditing ? (
          <AutocompleteFormField
            name="targetRateType"
            control={control}
            label={
              isReceivable
                ? t('approvals.driver_pay.tabs.driver_pay.customer_rate_type')
                : isInternalDriver
                  ? t('approvals.driver_pay.tabs.driver_pay.driver_rate_type')
                  : t('approvals.driver_pay.tabs.driver_pay.vendor_rate_type')
            }
            list={rateTypeOptions}
            errors={errors}
            isRequired={true}
            getLabel={(item) => item?.label ?? getRateTypeLabel(item)}
            getValue={(item) => item?.value ?? item}
            clearable
            onSelect={(rateType) => {
              setValue('targetRate', null);
              return rateType;
            }}
          />
        ) : (
          <PanelField
            label={
              isReceivable
                ? t('approvals.driver_pay.tabs.driver_pay.customer_rate_type')
                : isInternalDriver
                  ? t('approvals.driver_pay.tabs.driver_pay.driver_rate_type')
                  : t('approvals.driver_pay.tabs.driver_pay.vendor_rate_type')
            }
            content={
              invoice.rateType
                ? t(`administration.rates.${snakeCase(invoice.rateType)}`)
                : ''
            }
          />
        )}

        {isEditing ? (
          <TextFormField
            name="targetRateValue"
            control={control}
            label={
              isReceivable
                ? t('approvals.driver_pay.tabs.driver_pay.customer_rate')
                : isInternalDriver
                  ? t('approvals.driver_pay.tabs.driver_pay.driver_rate')
                  : t('approvals.driver_pay.tabs.driver_pay.vendor_rate')
            }
            errors={errors}
            type="number"
          />
        ) : (
          <PanelField
            label={
              isReceivable
                ? t('approvals.driver_pay.tabs.driver_pay.customer_rate')
                : isInternalDriver
                  ? t('approvals.driver_pay.tabs.driver_pay.driver_rate')
                  : t('approvals.driver_pay.tabs.driver_pay.vendor_rate')
            }
            content={getFormattedRateValue(
              invoice.rateType as RateType,
              invoice.rateValue ?? 0,
              currencyFormatter,
            )}
          />
        )}

        {hasPayableCommissionSide && (
          <PayCommissionControl
            key={String(isEditing)}
            control={control}
            name="targetPayCommissionPercentage"
            grossRate={targetRateValue}
            rateType={
              targetRateType?.value === invoice.rateType
                ? invoice.rateType
                : rateTypeOptions.find((option) => option.value === targetRateType?.value)
                    ?.value
            }
            initialValue={savedCommissionOverride}
            inheritedPercentage={commission.inheritedPercentage}
            savedNetRate={commission.netRate}
            savedGrossRate={invoice.rateValue}
            savedRateType={invoice.rateType}
            isDisabled={!isEditing || !canEditCommission}
          />
        )}
        {canEditLineItems && !isEditing && !isCustomerRateReadOnly ? (
          // Tooltip binds to the wrapper, not the button — MUI drops listeners on a disabled control.
          <Tooltip
            title={
              isRateApprovalLocked
                ? t('dispatch.job.rates.locked_by_approved_invoice')
                : ''
            }
          >
            <Box component="span" sx={{ display: 'inline-flex' }}>
              <IconButton
                aria-label={t('actions.edit')}
                color="secondary"
                data-ui="page-approvals__vendor-rate-panel__edit-button"
                onClick={() => handleEditChange(true)}
                disabled={!canEdit || isRateApprovalLocked}
                sx={{
                  border: `1px solid ${theme.brandV2.colors.treadGray7}`,
                  borderRadius: theme.spacing(0.5),
                  mt: 3,
                  ml: -1.5,
                }}
              >
                <Edit sx={{ height: 20, width: 20 }} />
              </IconButton>
            </Box>
          </Tooltip>
        ) : null}
      </Box>
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          alignItems: 'center',
          mt: 1,
        }}
      >
        {isEditing ? (
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              mt: 1,
            }}
          >
            <Button
              variant="outlined"
              color="secondary"
              data-ui="page-approvals__vendor-rate-panel__cancel-button"
              onClick={() => {
                resetToSavedRate();
                handleEditChange(false);
              }}
              disabled={isUpdating}
            >
              {t('actions.cancel')}
            </Button>

            <Button
              color="success"
              data-ui="page-approvals__vendor-rate-panel__save-button"
              loading={isUpdating}
              startIcon={<Check />}
              onClick={() => handleSubmit(onSubmit)()}
            >
              {t('actions.save')}
            </Button>
          </Box>
        ) : (
          <Box sx={{ alignItems: 'center', display: 'flex', gap: 1, mt: 1 }}>
            {hasSettlement && (
              <>
                {isSettlementApproved && (
                  <Chip
                    color="success"
                    hideDropdownArrow
                    label={t('approvals.driver_day.approved')}
                  />
                )}

                <LinkChip
                  label={getFriendlySettlementId(invoice.settlement!, category)}
                  href={`/${ROUTES.approvals.base}/${ROUTES.approvals.driverPay}?category=${category}&tab=settlements&search=${encodeURIComponent(getFriendlySettlementId(invoice.settlement!, category))}`}
                />
              </>
            )}

            {!hideInvoiceApproval && !hasSettlement && isApprovable && (
              <ApproveOrUnapproveCTA
                isBusy={isApprovingInvoice}
                onClick={handleApproveInvoiceClick}
                variant="approve"
              />
            )}

            {!hideInvoiceApproval && !hasSettlement && isUnapprovableInvoice && (
              <>
                <ApproveOrUnapproveCTA
                  isBusy={unapproveInvoiceState.isUnapproving}
                  onClick={() => {
                    setUnapproveInvoiceState({
                      isDialogOpen: true,
                      isUnapproving: false,
                    });
                  }}
                  variant="unapprove"
                />

                <ConfirmationDialog
                  confirmButtonProps={{
                    children: t(
                      'approvals.approve_jobs.unapprove_invoice_dialog.confirm_button',
                    ),
                  }}
                  isBusy={unapproveInvoiceState.isUnapproving}
                  isOpen={unapproveInvoiceState.isDialogOpen}
                  onClose={() => {
                    setUnapproveInvoiceState({
                      isDialogOpen: false,
                      isUnapproving: false,
                    });
                  }}
                  onConfirm={handleConfirmUnapproveInvoiceClick}
                  title={t('approvals.approve_jobs.unapprove_invoice_dialog.title')}
                >
                  <Typography>
                    {t('approvals.approve_jobs.unapprove_invoice_dialog.confirm_prompt')}
                  </Typography>

                  <Typography
                    sx={{
                      mt: 2,
                    }}
                  >
                    {t(
                      isLoadLevelPricingOn
                        ? 'approvals.approve_jobs.unapprove_invoice_dialog.sync_warning_rates_retained'
                        : 'approvals.approve_jobs.unapprove_invoice_dialog.sync_warning',
                    )}
                  </Typography>
                </ConfirmationDialog>
              </>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
});

interface PanelFieldProps {
  content: string;
  label: string;
  dataUi?: string;
}

const PanelField = ({ content, label, dataUi }: PanelFieldProps) => (
  <Box
    data-ui={dataUi}
    sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 0.5,
      width: '250px',
    }}
  >
    <Typography>{label}</Typography>
    <Typography
      sx={(theme) => ({
        bgcolor: theme.semantic.surfaceSunken,
        borderRadius: 1,
        fontWeight: 700,
        px: 2,
        py: 1,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        minHeight: '37px',
      })}
    >
      {content}
    </Typography>
  </Box>
);

interface ApproveOrUnapproveCTAProps {
  isBusy: boolean;
  onClick: () => void;
  variant: 'approve' | 'unapprove';
}

function ApproveOrUnapproveCTA({ isBusy, onClick, variant }: ApproveOrUnapproveCTAProps) {
  const menuHandler = useRef<MenuHandler>(null);

  if (variant === 'approve') {
    return (
      <Button color="brandV2Green" loading={isBusy} onClick={onClick}>
        {t('actions.approve')}
      </Button>
    );
  }

  return (
    <Menu
      ref={menuHandler}
      disableAutoFocusItem
      preventCloseCondition={isBusy}
      menuTrigger={
        <Box sx={{ cursor: 'pointer', display: 'inline-block' }}>
          <InvoiceApprovedChip />
        </Box>
      }
    >
      <UnapproveInvoiceMenuItem
        onClick={() => {
          menuHandler.current?.onClose?.();
          onClick();
        }}
      />
    </Menu>
  );
}

function InvoiceApprovedChip() {
  return <Chip color="success" label={t('approvals.driver_day.approved')} />;
}

interface UnapproveInvoiceMenuItemProps {
  onClick: () => void;
}

function UnapproveInvoiceMenuItem({ onClick }: UnapproveInvoiceMenuItemProps) {
  const theme = useTheme();

  return (
    <MenuItem onClick={onClick}>
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          gap: 1,
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <ArrowCircleDown
          sx={{
            color: theme.brandV2.colors.treadGray3,
            fontSize: '15px',
            transform: 'rotate(90deg)',
          }}
        />

        <OverflowAwareText sx={{ flex: 1 }}>
          {t('approvals.driver_day.unapprove')}
        </OverflowAwareText>
      </Box>
    </MenuItem>
  );
}

export default VendorRatePanel;
